import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import InfoAction from '@store/actions/information';
import EditedAction from '@store/actions/edited';
import CaptureAction from '@store/actions/capture';

import Page from '@lib/components/v2/Page';
import { ConfirmModal } from '@lib/components/v2/ConfirmModal';

import {
  checkIfCameraEnabled,
  geoLocation,
  getCookie,
  isShownPrivacy,
  setCookie
} from '@lib/Utils';
import APIs from '@services/APIs';
import { localizedString } from '@languages';

import { DigitalDLTimeLimitError } from '@FLOW_V2_FLOW/components/ErrorScreens/DigitalDLTimeLimitError';

import { withUseFullNameContextHook } from '@lib/components/v2/App/FullNameReviewProvider';

import { CaptureNSWDigitalDriverLicenceScreen } from '@lib/pages/v2/CaptureNSWDigitalDriverLicenceScreen';
import { ManualEntryScreen } from '@lib/pages/v2/ManualEntryScreen';
import { waitUntilLastCardStorageIsReady } from '@FLOW_V2_FLOW/services/storageCheckAPI';
import { getTransactionToken } from '@lib/utils/transactionToken';
import { isTimeLimitOKForDigitalScreenShot } from '@lib/utils/digitalDLCheck';
import {
  ENABLE_VISA_AFTER_PASSPORT,
  FLOW_V2_DOC_NOT_ACCEPTED_TRY_AGAIN_MODE,
  HIDE_FOOTER_UPLOAD_SPINNER,
  LOCATION,
  LOCATION_REQUIRED
} from '@spotMobileConfig';
import CaptureVisa from '@lib/components/v2/CaptureVisa';
import { BackOfCard, CaptureTips } from '@lib/components/v2/Contents';
import { Error500 } from '@lib/components/v2/errors';
import {
  CouldNotRecognise,
  DocNotAccepted,
  PoorQuality,
  Recapture
} from '@lib/components/v2/VerifyDetails/VerifyDetails.errors';
import { UseCaptureWrapper } from '@lib/components/v2/Capture/UseCaptureWrapper';
import { CaptureSideOfCard } from './CaptureSideOfCard';
import { EnableLocation, LocationSettings, Permission } from './Capture.error';
import { IdSelectionContent } from '../../components/Contents';
import { LoadingSpinner } from '../../components';

class Capture extends Component {
  static propTypes = {
    onNextStep: PropTypes.func,
    onGoBack: PropTypes.func,
    nextCapture: PropTypes.string,
    flowType: PropTypes.string,
    frontParams: PropTypes.object,
    selectedDiffId: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    isFlowV2DiffId: PropTypes.bool,
    captureVisa: PropTypes.bool,
    resetEditedFields: PropTypes.func,
    resetExtractedInfo: PropTypes.func,
    resetConfirmedInfo: PropTypes.func,
    onGeoLocation: PropTypes.func,
    setFrontIDParams: PropTypes.func,
    verify: PropTypes.bool,
    setDocId: PropTypes.func,
    onSelectLanguage: PropTypes.func,
    engine4Config: PropTypes.object,
    ipCountryCode: PropTypes.string,
    setExtractedInfo: PropTypes.func,
    setShouldDisplayFullName: PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.isComponentMounted = false;

    this.handleNextStep = this.handleNextStep.bind(this);
    this.handleGoBack = this.handleGoBack.bind(this);
    this.handleCapture = this.handleCapture.bind(this);
  }

  /**
   * Return the component's initial state
   * @return {Object}
   */
  getInitialState() {
    return {
      idType: 'AUS_AUTO_DRIVERLICENCE',
      progressBar: 0,
      backOfCard: false,
      isUploading: false,
      isLoading: false,
      isWaiting: false,
      error: null,
      geolocation: null,
      showCaptureVisa: false,
      acceptableDocs: false,
      showCaptureTips: '',
      showCaptureDigitalIdScreen: false,
      showManualEntryScreen: false,
      isExitConfirmModalDisplayed: false,
      showCaptureSideCard: false,
      sideCardHasBeenCaptured: false
    };
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    const { frontParams, nextCapture } = this.props;

    if (frontParams.idType) {
      if (nextCapture === 'back') {
        this.setState({
          backOfCard: true
        });
      }
    }
  }

  componentDidMount() {
    const {
      isFlowV2DiffId,
      captureVisa = false,
      selectedDiffId,
      resetEditedFields,
      resetExtractedInfo,
      resetConfirmedInfo,
      onGeoLocation
    } = this.props;
    this.isComponentMounted = true;

    this.checkCameraPermission();
    document.addEventListener('visibilitychange', this.checkCameraPermission);

    resetExtractedInfo();
    resetConfirmedInfo();
    resetEditedFields();

    if (captureVisa) {
      this.setState({
        showCaptureVisa: captureVisa,
        idType: selectedDiffId.type
      });
    }

    if (LOCATION)
      geoLocation()
        .then((geolocation) => {
          onGeoLocation(geolocation);
          this.setState({ geolocation });
        })
        .catch(() => {
          if (!LOCATION_REQUIRED) {
            onGeoLocation(null);
            return;
          }
          const error = {
            component: EnableLocation,
            props: {
              buttons: [
                {
                  label: localizedString('exit'),
                  variant: 'outline',
                  onClick: () => this.handleExit()
                },
                {
                  label: localizedString('enable'),
                  onClick: () => {
                    const error = {
                      component: LocationSettings,
                      props: {
                        buttons: [
                          {
                            label: localizedString('exit'),
                            variant: 'outline',
                            onClick: () => this.handleExit()
                          },
                          {
                            label: localizedString('checkAgainLabel'),
                            onClick: () => {
                              geoLocation()
                                .then((geolocation) =>
                                  this.setState({
                                    geolocation,
                                    error: null
                                  })
                                )
                                .catch(() => null);
                            }
                          }
                        ]
                      }
                    };
                    this.setState({ error });
                  }
                }
              ]
            }
          };
          this.setState({ error });
        });
    APIs.status('selectId', { resetTables: isFlowV2DiffId || captureVisa ? 'no' : 'yes' });
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    document.removeEventListener('visibilitychange', this.checkCameraPermission);
  }

  /**
   * Go to handle Select
   * @return {Void}
   */
  handleOnShowAcceptableDocsClick = () => {
    this.setState({ acceptableDocs: true });
  };

  /**
   * Go to the next step
   *
   */
  handleNextStep() {
    const { idType } = this.state;
    if (!idType) return;
    this.capture();
  }

  /**
   * Go back to the previous step
   * @param {ClickEvent} e
   * @return {Void}
   */
  handleGoBack(e) {
    e?.preventDefault();
    const { onGoBack } = this.props;
    const { acceptableDocs = false, backOfCard, showCaptureSideCard } = this.state;

    if (acceptableDocs) {
      this.setState({
        acceptableDocs: false
      });
    } else if (showCaptureSideCard) {
      this.setState({
        backOfCard: false,
        showCaptureSideCard: false
      });
    } else if (backOfCard) {
      this.setState({
        backOfCard: false,
        sideCardHasBeenCaptured: false
      });
      APIs.status('selectId', { resetTables: 'yes' });
    } else {
      const { backurl } = document.body.dataset;
      if (backurl && !isShownPrivacy('FLOW_V2')) {
        window.location.href = backurl;
      } else {
        onGoBack();
      }
    }
  }

  handleExit() {
    this.setState({ isExitConfirmModalDisplayed: true });
  }

  /**
   * When user happy with photo.
   * @param {ChangeEvent} e
   */
  handleCapture(e) {
    if (!e.target.files[0]) {
      return;
    }
    const imagefile = e.target.files[0];
    e.target.value = '';

    this.uploadImage(imagefile);
  }

  /**
   * Handle Visa Upload
   * @return {Void}
   */
  handleCaptureVisa = (e) => {
    if (!e.target.files[0]) {
      return;
    }
    const imagefile = e.target.files[0];
    e.target.value = '';

    this.uploadVisaImage(imagefile);
  };

  /**
   * Go to the next step on Visa Upload
   * @return {Void}
   */
  handleSkip = () => {
    const { geolocation, verifyDetails } = this.state;
    this.props.setFrontIDParams({});
    this.setState({
      showCaptureVisa: false
    });
    const nextStepOptions = { geolocation, verifyDetails };
    this.props.onNextStep(nextStepOptions);
  };

  handleNextStepAfterManualScreen() {
    const { onNextStep } = this.props;
    onNextStep({ isDataOnlyCompleted: true });
  }

  onClickedFooterTakePhoto = () => {
    const { backOfCard, isCameraEnabled } = this.state;
    this.checkCameraPermission();
    if (!isCameraEnabled) {
      const error = {
        component: Permission,
        props: {
          buttons: [
            {
              label: localizedString('back'),
              variant: 'transparent',
              onClick: () => this.setState({ error: null })
            },
            {
              label: localizedString('tryAgain'),
              onClick: () => this.setState({ error: null })
            }
          ]
        }
      };
      this.setState({ error });
      return;
    }

    if (backOfCard) {
      this.capture();
    } else {
      this.handleNextStep();
    }
  };

  onCaptureAgain() {
    this.setState({ showCaptureTips: '' });
    this.onClickedFooterTakePhoto();
  }

  checkCameraPermission = async () => {
    const isCameraEnabled = await checkIfCameraEnabled();
    this.setState({ isCameraEnabled });
  };

  // eslint-disable-next-line class-methods-use-this
  formatDay = (value) => {
    if (!value || value === '') {
      return '';
    }

    let parts;
    if (value.includes('-')) {
      parts = value.split('-');
    } else {
      parts = value.split('/');
    }

    const [day, month, year] = parts;
    return `${day}-${month}-${year}`;
  };

  handleStepAfterCaptureSideCard = () => {
    this.setState({ showCaptureSideCard: false, sideCardHasBeenCaptured: true });
  };

  uploadImage(imagefile, idType) {
    APIs.status('capturingId');
    this.setState({
      showCaptureVisa: false
    });
    const {
      frontParams,
      flowType,
      selectedDiffId,
      isFlowV2DiffId,
      setFrontIDParams,
      setShouldDisplayFullName
    } = this.props;
    const { backOfCard } = this.state;

    let idCaptureAttempt = parseInt(getCookie('idCaptureAttempt'), 10);
    idCaptureAttempt = idCaptureAttempt ? idCaptureAttempt + 1 : 1;
    setCookie('idCaptureAttempt', idCaptureAttempt.toString(), 1);

    let params;
    if (backOfCard) {
      params = { ...frontParams, backFile: imagefile };
      setFrontIDParams({});
    } else {
      params = { frontFile: imagefile };
      setFrontIDParams(params);
    }
    params.flowType = flowType;
    params.isDiffId = isFlowV2DiffId && !!selectedDiffId;

    this.setState({
      isUploading: true,
      backOfCard: false,
      acceptableDocs: false,
      error: null,
      progressBar: 0
    });
    APIs.uploadImage(params, {
      onProgress: (width) => this.setState({ progressBar: width })
    })
      .then(({ status, token }) => {
        const { isUploading } = this.state;
        if (!isUploading) {
          return;
        }

        if (status === 'error') {
          if (this.isComponentMounted) {
            this.setState({
              error: {
                component: CouldNotRecognise,
                props: {
                  buttons: [
                    {
                      label: localizedString('tryAgain'),
                      onClick: () => {
                        setFrontIDParams({});
                        this.setState({ error: null });
                      }
                    }
                  ]
                }
              },
              isUploading: false
            });
          }
          return;
        }

        this.setState({ isUploading: false });

        if (token) {
          const noBack = !backOfCard;

          this.setState({ isLoading: true });
          const otherParams = {
            isEngineV4: true,
            isDiffId: isFlowV2DiffId && !!selectedDiffId,
            flowType
          };

          if (typeof selectedDiffId === 'object' && selectedDiffId !== null) {
            otherParams.idType = selectedDiffId.type;
          }

          if (idType) {
            otherParams.idType = idType;
          }

          APIs.extractIdentifyInfo(token, noBack, otherParams)
            .then(
              async ({
                data: cardData,
                code,
                poorQuality = null,
                docAccepted = true,
                captureBackCard = false,
                captureSideCard = false,
                ignoreBackCardOfAUStates = ['NSW', 'TAS'],
                useIdNumber = false,
                isOldTurkishDL = false,
                poll = false,
                disableVerifyDetailsScreen = false,
                disabledVerifyDetailsFields,
                disabledFields,
                personNameFieldsOrder,
                displayFullName = false
              }) => {
                setShouldDisplayFullName(displayFullName);

                const { isLoading } = this.state;
                if (!isLoading) {
                  return;
                }

                if (poll) {
                  const transactionToken = getTransactionToken();
                  await waitUntilLastCardStorageIsReady({ transactionToken });
                }

                if (!isTimeLimitOKForDigitalScreenShot(cardData)) {
                  const buttons = [
                    {
                      label: localizedString('tryAgain'),
                      onClick: () => {
                        this.setState({ error: null, showCaptureDigitalIdScreen: true });
                      }
                    }
                  ];

                  const error = {
                    component: DigitalDLTimeLimitError,
                    props: { buttons }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                let buttons = [
                  {
                    label: localizedString('back'),
                    variant: 'transparent',
                    onClick: () => {
                      setFrontIDParams({});
                      this.setState({ error: null });
                      APIs.status('selectId', { resetTables: 'yes' });
                    },
                    dataTestId: 'capture-backBtn'
                  }
                ];

                if (docAccepted === false) {
                  if (FLOW_V2_DOC_NOT_ACCEPTED_TRY_AGAIN_MODE) {
                    buttons = [
                      {
                        label: localizedString('tryAgain'),
                        onClick: () => {
                          setFrontIDParams({});
                          this.setState({ error: null });
                        }
                      }
                    ];
                  } else {
                    buttons.push({
                      label: localizedString('viewAcceptedIDs'),
                      onClick: () => {
                        setFrontIDParams({});
                        this.setState({ error: null, acceptableDocs: true });
                      }
                    });
                  }

                  const error = {
                    component: DocNotAccepted,
                    props: { buttons }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                const viewTipsButtonLabel = localizedString('capture.FLOW_V2_VIEW_TIPS_BUTTON');

                if (code === '223') {
                  buttons.push({
                    label: localizedString('tryAgain'),
                    onClick: () => {
                      setFrontIDParams({});
                      this.setState({ error: null });
                    }
                  });
                  const error = {
                    // component: FaceNotDetectedInId,
                    component: CouldNotRecognise,
                    props: { buttons }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                if (code === '220' || code === '230' || code === '240' || code === '270') {
                  buttons.push({
                    label: viewTipsButtonLabel,
                    onClick: () => {
                      setFrontIDParams({});
                      this.setState({ error: null, showCaptureTips: 'CouldntRecognise' });
                    }
                  });
                  const error = {
                    component: CouldNotRecognise,
                    props: { buttons }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                if (code !== '200') {
                  buttons.push({
                    // label: localizedString("tryAgain"),
                    label: viewTipsButtonLabel,
                    onClick: () => {
                      setFrontIDParams({});
                      // this.setState({ error: null })
                      this.setState({ error: null, showCaptureTips: 'CouldntRecognise' });
                    }
                  });
                  const error = {
                    // component: CouldNotRead,
                    component: CouldNotRecognise,
                    props: { buttons }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                if (poorQuality) {
                  buttons.push({
                    label: viewTipsButtonLabel,
                    onClick: () => {
                      setFrontIDParams({});
                      this.setState({ error: null, showCaptureTips: 'PoorQuality' });
                    },
                    dataTestId: 'betterImg-viewTipsBtn'
                  });
                  const error = {
                    component: PoorQuality,
                    props: {
                      buttons,
                      cropped: !poorQuality || poorQuality === 'NULL' ? imagefile : poorQuality
                    }
                  };
                  this.setState({
                    error,
                    isLoading: false,
                    progressBar: 0
                  });
                  return;
                }

                const country = cardData.countryCode;

                if (!params.backFile && captureBackCard) {
                  if (
                    cardData.cardType.match(/LICENCE/i) &&
                    country === 'AU' &&
                    ignoreBackCardOfAUStates.includes(cardData.state)
                  ) {
                    // Skip
                  } else if (cardData.cardType.match(/LICENCE/i) && country === 'UK') {
                    // Skip
                  } else {
                    this.setState({
                      idType: cardData.cardType,
                      isLoading: false,
                      progressBar: 0,
                      backOfCard: true
                    });
                    this.checkIfCaptureSideCardShouldBeShown(captureSideCard);
                    this.handleStepAfterCaptureSideCard = () => {
                      this.setState({ showCaptureSideCard: false, sideCardHasBeenCaptured: true });
                    };
                    return;
                  }
                }

                if (this.checkIfCaptureSideCardShouldBeShown(captureSideCard)) {
                  this.handleStepAfterCaptureSideCard = () => {
                    this.setState({ showCaptureSideCard: false, sideCardHasBeenCaptured: true });
                    const { verify } = this.props;
                    if (verify) {
                      APIs.status('checkDocument');
                    }
                    this.processExtractedData({
                      cardData,
                      useIdNumber,
                      isOldTurkishDL,
                      disableVerifyDetailsScreen,
                      disabledVerifyDetailsFields,
                      disabledFields,
                      personNameFieldsOrder,
                      token,
                      skipPreparingCardsUserUpdate: true
                    });
                  };
                  return;
                }

                this.processExtractedData({
                  cardData,
                  useIdNumber,
                  isOldTurkishDL,
                  disableVerifyDetailsScreen,
                  disabledVerifyDetailsFields,
                  disabledFields,
                  personNameFieldsOrder,
                  token
                });
              }
            )
            .catch((e) => {
              console.error(e);

              const error = {
                component: Error500,
                props: {
                  onTryAgain: () => {
                    setFrontIDParams({});
                    this.setState({
                      error: null,
                      isUploading: false,
                      isLoading: false,
                      isWaiting: false,
                      backOfCard: false
                    });
                  }
                }
              };
              this.setState({ error });
            });
        }
      })
      .catch(() => {
        const error = {
          component: Error500,
          props: {
            onTryAgain: () => {
              setFrontIDParams({});
              this.setState({
                error: null,
                isUploading: false,
                isLoading: false,
                isWaiting: false,
                backOfCard: false
              });
            }
          }
        };

        setFrontIDParams({});
        this.setState({ error });
      });
  }

  async processExtractedData({
    cardData,
    useIdNumber,
    isOldTurkishDL,
    disableVerifyDetailsScreen,
    disabledVerifyDetailsFields,
    disabledFields,
    personNameFieldsOrder,
    token,
    skipPreparingCardsUserUpdate = false
  }) {
    const { verify, setFrontIDParams, setDocId, onNextStep, setExtractedInfo } = this.props;
    const { geolocation, backOfCard } = this.state;
    const country = cardData.countryCode;

    const temp = cardData;
    temp.dateOfBirth = this.formatDay(temp.dateOfBirth);
    temp.expiryDate = this.formatDay(temp.expiryDate);

    temp.countryOfIssue = cardData.country;
    setDocId(temp.documentId);

    if (
      (cardData.cardType === 'Passport' || cardData.cardType === 'MRZ') &&
      ENABLE_VISA_AFTER_PASSPORT &&
      country &&
      country.toUpperCase() !== 'NZ' &&
      country.toUpperCase() !== 'AU'
    ) {
      setExtractedInfo(temp);
      let verifyDetails = { ...temp };
      if (cardData.givenNames) {
        const [firstName, middleName] = cardData.givenNames.split(' ');
        verifyDetails = { ...verifyDetails, firstName, middleName };
      }
      this.setState({
        idType: 'PASSPORT',
        isLoading: false,
        progressBar: 0,
        showCaptureVisa: true,
        verifyDetails
      });
      return;
    }

    // Validate Address
    if (!verify) {
      if (!disableVerifyDetailsScreen) {
        APIs.status('reviewInfo');
      }
      setExtractedInfo(temp);
      onNextStep({
        tokenId: token,
        geolocation,
        idType: temp.cardType,
        backOfCard,
        verifyDetails: temp,
        useIdNumber,
        personNameFieldsOrder,
        isOldTurkishDL,
        captureVisa: false,
        disableVerifyDetailsScreen,
        disabledVerifyDetailsFields,
        disabledFields
      });
    } else {
      this.setState({
        isLoading: false,
        isWaiting: true
      });

      const promise = skipPreparingCardsUserUpdate
        ? Promise.resolve()
        : APIs.status('preparingCards');

      await promise.then(() => {
        return APIs.checkApproval().then((status) => {
          const { isWaiting } = this.state;
          if (!isWaiting) {
            return;
          }

          this.setState({ isWaiting: false });
          if (status !== 'approved') {
            const error = {
              component: Recapture,
              props: {
                buttons: [
                  {
                    label: localizedString('recapture'),
                    onClick: () => {
                      setFrontIDParams({});
                      this.handleNextStep();
                    }
                  }
                ]
              }
            };
            this.setState({ error, isLoading: false });
            return;
          }
          if (!disableVerifyDetailsScreen) {
            APIs.status('reviewInfo');
          }
          setExtractedInfo(temp);
          onNextStep({
            tokenId: token,
            geolocation,
            idType: temp.cardType,
            backOfCard,
            verifyDetails: temp,
            useIdNumber,
            personNameFieldsOrder,
            isOldTurkishDL,
            disableVerifyDetailsScreen,
            disabledVerifyDetailsFields,
            disabledFields
          });
        });
      });
    }
  }

  uploadVisaImage(imagefile) {
    APIs.status('capturingId');
    const { setFrontIDParams, onNextStep, onGoBack, flowType } = this.props;
    const { geolocation, verifyDetails } = this.state;
    const params = {
      visa: 'yes',
      idType: 'VISA',
      flowType,
      frontFile: imagefile
    };

    this.setState({ isUploading: true, showCaptureVisa: false, progressBar: 0 });

    APIs.uploadImage(params, {
      before: () => this.setState({ progressBar: 0 }),
      onProgress: (width) => {
        this.setState({ progressBar: width });
      }
    })
      .then(({ status, skip = false }) => {
        const nextStepOptions = { geolocation, verifyDetails };

        if (status === 'error') {
          this.setState({
            error: {
              component: CouldNotRecognise,
              props: {
                buttons: [
                  {
                    label: localizedString('tryAgain'),
                    onClick: () => {
                      setFrontIDParams({});
                      this.setState({ error: null });
                    }
                  }
                ]
              }
            },
            isUploading: false
          });
          if (skip) {
            setFrontIDParams({});
            onNextStep(nextStepOptions);
          }
          return;
        }

        this.setState({
          isUploading: false
        });

        setFrontIDParams({});
        onNextStep(nextStepOptions);
      })
      .catch(() => {
        const error = {
          component: Error500,
          props: {
            onTryAgain: () => {
              setFrontIDParams({});
              onGoBack();
            }
          }
        };
        this.setState({ error, isUploading: false });
      });
  }

  checkIfCaptureSideCardShouldBeShown(captureSideCard) {
    const { sideCardHasBeenCaptured } = this.state;
    if (captureSideCard && !sideCardHasBeenCaptured) {
      this.setState({ showCaptureSideCard: true, isLoading: false, progressBar: 0 });
      return true;
    }
    return false;
  }

  render() {
    const { backurl } = document.body.dataset;
    const {
      progressBar,
      backOfCard,
      isUploading,
      isLoading,
      isWaiting,
      error,
      showCaptureVisa,
      idType,
      acceptableDocs = false,
      showCaptureTips = '',
      showCaptureDigitalIdScreen,
      showManualEntryScreen,
      isExitConfirmModalDisplayed,
      showCaptureSideCard,
      isCameraEnabled
    } = this.state;

    const { component: Error, props: errorProps } = error || {};
    const {
      onGoBack,
      onSelectLanguage,
      engine4Config,
      ipCountryCode,
      flowType,
      isFlowV2DiffId,
      captureVisa = false
    } = this.props;

    const captureVisaProps = {
      onSkip: this.handleSkip,
      onUploadVisa: this.handleNextStep
    };

    const footerButtons = [];
    if (!Error) {
      if (isUploading || isLoading || isWaiting || isCameraEnabled === undefined) {
        footerButtons.push({
          label: '',
          variant: 'transparent',
          dataTestId: 'cptrId-back',
          'aria-hidden': true
        });

        if (!HIDE_FOOTER_UPLOAD_SPINNER) {
          footerButtons.push({
            label:
              isUploading && progressBar < 100
                ? localizedString('uploading')
                : localizedString('loading'),
            variant: 'transparent',
            loading: true,
            dataTestId: 'id-Uploading'
          });
        }
      } else {
        if (backurl || isShownPrivacy('FLOW_V2') || acceptableDocs) {
          footerButtons.push({
            label: localizedString('back'),
            variant: 'transparent',
            onClick: this.handleGoBack,
            dataTestId: 'cptrId-back'
          });
        }

        footerButtons.push({
          label: localizedString('captureMyID'),
          type: 'submit',
          onClick: () => this.onClickedFooterTakePhoto(),
          dataTestId: 'cptrId-btn'
        });
      }
    }

    const areOtherScreensOff =
      !showCaptureVisa &&
      !showCaptureDigitalIdScreen &&
      !showManualEntryScreen &&
      !Error &&
      showCaptureTips === '';

    return (
      <UseCaptureWrapper
        upload={showCaptureVisa ? this.handleCaptureVisa : this.handleCapture}
        inputCaptureType="environment"
      >
        {({ capture }) => {
          this.capture = capture;

          return (
            <div>
              {isExitConfirmModalDisplayed && (
                <ConfirmModal
                  heading={localizedString('app.FLOW_V2_EXIT_SCREEN_TITLE')}
                  onConfirm={() => onGoBack()}
                  onCancel={() => this.setState({ isExitConfirmModalDisplayed: false })}
                >
                  {localizedString('app.FLOW_V2_EXIT_SCREEN_DESCRIPTION_CAPTURE')}
                </ConfirmModal>
              )}
              {Error && <Error {...errorProps} />}
              {showCaptureTips !== '' && (
                <CaptureTips
                  type={showCaptureTips}
                  onHide={() => this.setState({ showCaptureTips: '' })}
                  resetTables={isFlowV2DiffId || captureVisa ? 'no' : 'yes'}
                  onCaptureAgain={() => this.onCaptureAgain()}
                />
              )}
              {showCaptureVisa && <CaptureVisa {...captureVisaProps} />}
              {showCaptureDigitalIdScreen && (
                <CaptureNSWDigitalDriverLicenceScreen
                  onUploadDigitalImage={({ imageFile, idType }) => {
                    this.uploadImage(imageFile, idType);
                    this.setState({ showCaptureDigitalIdScreen: false });
                  }}
                  onCloseScreen={() => this.setState({ showCaptureDigitalIdScreen: false })}
                />
              )}
              {showManualEntryScreen && (
                <ManualEntryScreen
                  onComplete={() => this.handleNextStepAfterManualScreen()}
                  onCloseManualEntryScreen={() => this.setState({ showManualEntryScreen: false })}
                  flowType={flowType}
                />
              )}
              {areOtherScreensOff &&
                (showCaptureSideCard ? (
                  <CaptureSideOfCard
                    onBack={() => this.handleGoBack()}
                    onNext={() => this.handleStepAfterCaptureSideCard()}
                  />
                ) : (
                  <Page
                    // isDark={backOfCard}
                    buttons={footerButtons}
                    forceFillViewPort
                    onSelectLanguage={onSelectLanguage}
                  >
                    {backOfCard && <BackOfCard idType={idType} />}
                    {isWaiting && (
                      <LoadingSpinner heading={localizedString('waitingForApproval')} />
                    )}
                    {!backOfCard && (
                      <IdSelectionContent
                        idType={idType}
                        acceptableDocs={acceptableDocs}
                        onShowCaptureDigitalIdScreen={() =>
                          this.setState({ showCaptureDigitalIdScreen: true })
                        }
                        onSelectManualEntry={() => this.setState({ showManualEntryScreen: true })}
                        isLoading={isUploading || isLoading || isWaiting}
                        progressBar={progressBar}
                        engine4Config={engine4Config}
                        ipCountryCode={ipCountryCode}
                        onShowAcceptableDocsClick={this.handleOnShowAcceptableDocsClick}
                      />
                    )}
                  </Page>
                ))}
            </div>
          );
        }}
      </UseCaptureWrapper>
    );
  }
}

export default withUseFullNameContextHook(connect(mapStateToProps, mapDispatchToProps)(Capture));

function mapStateToProps({ capture, appConfig, ipInfo }) {
  return {
    frontParams: capture.frontParams,
    engine4Config: appConfig.engine4 ? appConfig.engine4.FLOW_V2 : null,
    ipCountryCode: ipInfo.ipCountryCode
  };
}

/**
 * Map the dispatch function of the store to the component's props
 * @param  {Function} dispatch The dispatch function
 * @return {Object}
 */
function mapDispatchToProps(dispatch) {
  return {
    setExtractedInfo: (data) => dispatch(InfoAction.setExtractedInfo(data)),
    resetExtractedInfo: () => dispatch(InfoAction.resetExtractedInfo()),
    resetConfirmedInfo: () => dispatch(InfoAction.resetConfirmedInfo()),
    resetEditedFields: () => dispatch(EditedAction.setEditedFields([])),
    setFrontIDParams: (data) => dispatch(CaptureAction.setFrontIDParams(data))
  };
}
